import { _keys, asArray, BadImplementationException, compare, exists, filterInstances, lastElement, Module, Second, sortArray, voidFunction } from '@nu-art/ts-common';
import { contextCloseMode_Force, contextCloseMode_Safe, provideInfoContextKey_Expression, provideInfoContextKey_Protocol, provideInfoContextKey_Score } from './types.js';
import { getProvideInfoContextData_Expression, getProvideInfoContextData_Protocol, getProvideInfoContextData_Score, provideInfo_GetAllStepVariableIds, provideInfo_GetInputScope_Expression, provideInfo_GetInputScope_Protocol, provideInfo_GetQuestionValues } from './helpers.js';
import { findingSourceTypesEnum, FindingType_Numeric, } from '@app/advisor-shared';
import { ModuleFE_ExplainV2 } from '../explain/ModuleFE_ExplainV2.js';
import { ModuleFE_AdvisorBoard } from '../../_enum.js';
import { dispatch_OnProvideInfoContextChange, dispatch_OnProvideInfoContextUpdate, dispatch_OnProvideInfoExplain } from './dispatchers.js';
import { ModuleFE_Advisor_Expression, ModuleFE_Advisor_Variable } from '@app/advisor-frontend/modules/static-modules/healthcare-space';
import { ModuleFE_AdvisorAnalytics } from '../../_analytics/consts.js';
import { ModuleFE_AdvisorMini } from '../ModuleFE_AdvisorMini.js';
import { ProtocolData } from '../../_utils/ProtocolData/ProtocolData.js';
import { resolve_EMSuggestedFindings, resolve_ProvideInfoFindings_EMFindings } from './_utils/em-resolving.js';
import { supplementPreviousContextFindings } from './_utils/data-supplementing.js';
import { ColumnKey_LeftColumn, ColumnKey_MiddleColumn, HomePanelHeight, LeftColumn_AllClosed, MiddleColumn_Findings, MiddleColumn_ProvideInfo, PanelKey_Home, PanelKey_HPI, PanelKey_Inbox, PanelKey_EventFeed, PanelKey_ProtocolInfo, PanelKey_Settings, PanelKey_SmartDocs, SettingPanelHeight } from '@app/advisor-v5-shared';
import { ModuleFE_EMInterface, ModuleFE_RuntimeFinding, ModuleFE_RuntimeProtocol } from '@app/advisor-frontend';
import { ExpressionType_Score, variableType_AttributeInstance } from '@app/hcs-shared';
import { Toaster_AdvisorGeneral } from '../../ui/pages/toast.js';
import { calculate_ExpressionValueTree } from '@app/hcs-frontend/ui/_components/expression-pretty-print/Component_PrettyPrint_Expression/value-tree';
import { Dialog_DiscardProvideInfo } from '../../ui/dialogs/Dialog_DiscardProvideInfo/Dialog_DiscardProvideInfo.js';
import { Toaster_ProtocolNavigation } from './toasting.js';
import { TransitionCondition } from '@app/pathway-shared';
const protocolContext_LeftColumn = [
    { height: HomePanelHeight, panelKey: PanelKey_Home },
    { height: 1, panelKey: PanelKey_ProtocolInfo },
    { height: 0, panelKey: PanelKey_HPI },
    { height: 0, panelKey: PanelKey_Inbox },
    { height: 0, panelKey: PanelKey_SmartDocs },
    { height: 0, panelKey: PanelKey_EventFeed },
    { height: SettingPanelHeight, panelKey: PanelKey_Settings },
];
class ModuleFE_ProvideInfo_Class extends Module {
    contextChain;
    explainMode = false;
    protocolSubmissionInProgress = false;
    muteSimulationListeners = false;
    showDetailsOnOpen = false;
    __onRuntimeProtocolsUpdated = (...params) => {
        if (this.protocolSubmissionInProgress)
            return;
        const currentContextData = this.getCurrentProtocolContextData();
        if (!currentContextData) //Not currently in a protocol context
            return;
        const updatedProtocol = asArray(params[1]).find(p => p._id === currentContextData.protocolData.protocol._id);
        const updatedProtocolData = ProtocolData.createNew(updatedProtocol?._id);
        if (!updatedProtocol || !updatedProtocolData) //Current context was not updated
            return;
        //Check if the protocol has:
        //1. Navigated to a different state
        //2. Has a change in needed variables
        //In both of these situations, we can re-open the protocol context and toast to let users know
        const currentActiveState = currentContextData.protocolData.getActiveState();
        const updatedActiveState = updatedProtocolData.getActiveState();
        if (currentActiveState?._id !== updatedActiveState?._id || !compare(updatedProtocolData.protocol.neededVariables, currentContextData.protocolData.protocol.neededVariables)) {
            this.reOpenProtocolContext();
            if (!Toaster_ProtocolNavigation.getQueueLength())
                Toaster_ProtocolNavigation.toastGeneral({
                    title: 'Protocol navigation re-opened',
                    body: `${currentContextData.protocolData.getLabel()} information updated - decision making re-opened to reflect new data`,
                    duration: 5 * Second,
                });
        }
    };
    __onExpressionCalculation = (response) => {
        if (this.muteSimulationListeners)
            return;
        const currentContext = lastElement(this.contextChain);
        if (!currentContext || currentContext.key === provideInfoContextKey_Protocol)
            return;
        switch (currentContext.key) {
            case provideInfoContextKey_Score:
                return this._score.handleSimulationResponse(response);
            case provideInfoContextKey_Expression:
                return this._expression.handleSimulationResponse(response);
        }
    };
    __onProtocolNavigationSimulation = (response) => {
        if (this.muteSimulationListeners)
            return;
        this._protocol.handleSimulationResponse(response);
    };
    __onPathwayTransitionSelected = (pathwayTransitionId) => {
        const focused = this.protocol.focusStep2(pathwayTransitionId);
        if (!focused)
            return;
        this.protocol.toggleNavMode(true);
    };
    //######################### Internal Logic #########################
    openContext(context, resetContextChain) {
        const postOpen = () => {
            dispatch_OnProvideInfoContextChange.dispatchAll(context);
            //If the context is not protocol / the protocol context is in nav mode, open the provide info panel
            if (context.key !== provideInfoContextKey_Protocol || context.data.navMode)
                ModuleFE_AdvisorBoard.setColumnItems(ColumnKey_MiddleColumn, MiddleColumn_ProvideInfo);
            else //This is a protocol context not in nav mode, close the panel
                ModuleFE_AdvisorBoard.setColumnItems(ColumnKey_MiddleColumn, MiddleColumn_Findings);
        };
        if (resetContextChain) { //Resetting the chain, do safely
            return this.closeContextChain(contextCloseMode_Safe, () => {
                this.contextChain = [context];
                return postOpen();
            });
        }
        else { //Not resetting
            if (!this.contextChain)
                this.contextChain = [];
            //Derive findings from previous context
            const previousContext = lastElement(this.contextChain);
            if (previousContext)
                this.deriveRawFindingsToContext(context, previousContext.data.findings.item.toCreate);
            this.contextChain.push(context);
            postOpen();
        }
    }
    deriveRawFindingsToContext = (context, rawFindings) => {
        context.data.findings.item.toCreate?.forEach(prf => {
            const existing = rawFindings.find(existing => existing.variableId === prf.variableId);
            if (!existing)
                return;
            switch (prf.type) {
                case 'numeric':
                    prf.value = existing.value;
                    break;
                case 'enumerated':
                    prf.valueIds = existing.valueIds;
                    prf.attributes?.forEach(att => {
                        const existingAtt = existing.attributes?.find(existingAtt => existingAtt.variableId === att.variableId);
                        if (existingAtt)
                            att.valueIds = existingAtt.valueIds;
                    });
            }
        });
    };
    reOpenProtocolContext = () => {
        const currentProtocolContextData = this.getCurrentProtocolContextData();
        if (!currentProtocolContextData)
            return;
        this.closeContextChain(contextCloseMode_Force);
        this.protocol.open(currentProtocolContextData.protocolData.protocol._id, true);
    };
    getPartialCalculationFindings = (data, response) => {
        const newPartialCalculations = response.findings.updated.filter(f => !!f.isPartialCalculation);
        const fallbackPartialCalculations = filterInstances(data.findings.item.toCreate?.map(prf => {
            if (prf.type !== FindingType_Numeric || newPartialCalculations.find(item => item.variableId === prf.variableId))
                return;
            const existingPartials = ModuleFE_RuntimeFinding.cache.filter(f => !!f.isPartialCalculation
                && f.variableId === prf.variableId);
            return sortArray(existingPartials, 'issuedTimestamp', true)[0];
        }) ?? []);
        return [...newPartialCalculations, ...fallbackPartialCalculations];
    };
    closeContextChain_Impl = () => {
        ModuleFE_ExplainV2.clearSimulations();
        this.explainMode = false;
        this.contextChain = undefined;
        dispatch_OnProvideInfoContextChange.dispatchAll();
        ModuleFE_AdvisorBoard.setColumnItems(ColumnKey_MiddleColumn, MiddleColumn_Findings);
        ModuleFE_AdvisorBoard.setColumnItems(ColumnKey_LeftColumn, LeftColumn_AllClosed);
        setTimeout(() => {
            if (ModuleFE_AdvisorMini.getIsMiniActive() && !this.contextChain?.length)
                ModuleFE_AdvisorMini.openPanel(PanelKey_Home);
        });
    };
    wrapUpContextChain = async () => {
        let context;
        do {
            if (exists(context) && this.getLatestContext() === context)
                throw new Error('Could not close context');
            context = this.getLatestContext();
            if (!context)
                continue;
            ModuleFE_ExplainV2.clearSimulations();
            const findings = context.data.findings.item;
            switch (context.key) {
                case provideInfoContextKey_Expression: {
                    const previousContext = this.contextChain[this.contextChain.length - 2];
                    if (!previousContext)
                        throw new BadImplementationException('Wrapping up expression in the chain with no previous context');
                    await this._expression.wrapUp(context, previousContext);
                    this.contextChain.pop();
                    break;
                }
                case provideInfoContextKey_Score: {
                    const previousContext = this.contextChain[this.contextChain.length - 2];
                    if (!previousContext) {
                        await this._score.submit(findings);
                        this.context.current.close(contextCloseMode_Force);
                    }
                    else {
                        await this._score.wrapUp(context, previousContext);
                        this.contextChain.pop();
                    }
                    break;
                }
                case provideInfoContextKey_Protocol: {
                    await this._protocol.wrapUp(findings);
                    this.context.current.close(contextCloseMode_Force);
                }
            }
        } while (exists(context));
    };
    closeContextChain = (mode, onChainWrappedUp = this.closeContextChain_Impl) => {
        if (!this.contextChain?.length) {
            if (onChainWrappedUp !== this.closeContextChain_Impl)
                onChainWrappedUp();
            return;
        }
        const closeAndCB = () => {
            this.closeContextChain_Impl();
            if (onChainWrappedUp !== this.closeContextChain_Impl)
                onChainWrappedUp();
        };
        switch (mode) {
            case 'force': {
                return closeAndCB();
            }
            case 'safe':
                const hasChanges = this.context.chain.hasChanges();
                if (!hasChanges)
                    return closeAndCB();
                Dialog_DiscardProvideInfo.show({
                    onDiscard: () => closeAndCB(),
                    onSave: async () => {
                        try {
                            await this.wrapUpContextChain();
                            onChainWrappedUp();
                        }
                        catch (err) {
                            this.logError(err);
                        }
                    }
                });
        }
    };
    //######################### Logic - Expression #########################
    _expression = {
        open: (expressionId, neededVariable) => {
            //Opening an expression context can only be done from another context, so it never resets the chain
            const expression = ModuleFE_Advisor_Expression.cache.unique(expressionId);
            if (!expression)
                return this.logWarning(`Could not find expression with id ${expressionId}`);
            //Expression is a score, continue with opening score
            if (expression.expressionType === ExpressionType_Score.dbKey)
                return this._score.open(expressionId, neededVariable);
            const prevContext = lastElement(this.contextChain);
            if (!prevContext)
                throw new BadImplementationException('Opening an expression context with no parent context');
            ModuleFE_ExplainV2.clearSimulations();
            const data = getProvideInfoContextData_Expression(expression, neededVariable.innerVariables ?? [], this._expression.submit, prevContext.data.findings.item);
            //Trigger a calculation simulation when changing the findings
            data.findings.setOnChanged(async (findings) => {
                ModuleFE_ExplainV2.simulate_ExpressionResult([expressionId], resolve_ProvideInfoFindings_EMFindings(findings.item), false, () => this.context.current.updateCalcInProgress(false));
                this.context.current.updateCalcInProgress(true);
            });
            this.openContext({ key: provideInfoContextKey_Expression, data }, false);
            //Simulate calculation
            ModuleFE_ExplainV2.simulate_ExpressionResult([expressionId], resolve_ProvideInfoFindings_EMFindings(data.findings.item), true)
                .then(response => this._expression.handleSimulationResponse(response))
                .catch(err => this.logError('Failed to simulate expression', err));
        },
        handleSimulationResponse: (response, closeInputIfScopeIsEmpty = false) => {
            const context = this.getLatestContext();
            if (context?.key !== provideInfoContextKey_Expression)
                return;
            const data = context.data;
            const calculationResult = response.calculationResults[data.expression._id];
            if (!calculationResult)
                return;
            data.calculationInProgress = false;
            data.calculationResult = calculationResult;
            data.partialCalculationFindings = this.getPartialCalculationFindings(data, response);
            if (data.expressionDescription)
                data.valueTree = calculate_ExpressionValueTree(data.expressionDescription, data.calculationResult);
            this.context.utils.recalculateInputScope(context);
            if (closeInputIfScopeIsEmpty && !data.input.scope?.length) {
                data.input.show = false;
                data.input.varId = undefined;
            }
            else if (data.input.scope?.length) {
                data.input.varId = data.input.scope[0];
            }
            dispatch_OnProvideInfoContextUpdate.dispatchAll(context);
        },
        wrapUp: async (context, previousContext) => {
            if (context.key !== provideInfoContextKey_Expression)
                throw new BadImplementationException('Called expression wrap up with non expression context');
            //Clear all current simulations
            ModuleFE_ExplainV2.clearSimulations();
            try {
                //Simulate answer and update previous context
                const expressionId = context.data.expression._id;
                const currentFindings = context.data.findings.item;
                const previousFindings = previousContext.data.findings.item;
                const normalizedEMFindings = resolve_ProvideInfoFindings_EMFindings(currentFindings);
                const response = await ModuleFE_ExplainV2.simulate_ExpressionResult([expressionId], normalizedEMFindings, true);
                supplementPreviousContextFindings(currentFindings, previousFindings, response.findings);
            }
            catch (err) {
                this.logError('Failed simulating expression result', err);
            }
        },
        submit: async (findings) => {
            const currentContext = lastElement(this.contextChain);
            if (currentContext?.key !== provideInfoContextKey_Expression)
                throw new BadImplementationException(`Called expression-submit with non expression current context: ${currentContext?.key}`);
            const previousContext = this.contextChain?.[this.contextChain?.length - 2];
            if (!previousContext)
                throw new BadImplementationException('Called expression-submit with no previous context');
            try {
                await this._expression.wrapUp(currentContext, previousContext);
                this.context.current.close(contextCloseMode_Force);
                return findings;
            }
            catch (err) {
                this.logError('Failed simulating expression', err);
                return findings;
            }
        }
    };
    //######################### Logic - Score #########################
    _score = {
        open: (expressionId, neededVariable, resetContextChain = false) => {
            const expression = ModuleFE_Advisor_Expression.cache.unique(expressionId);
            if (!expression)
                return this.logWarning(`Could not find expression with id ${expressionId}`);
            ModuleFE_ExplainV2.clearSimulations();
            const data = getProvideInfoContextData_Score(expression, neededVariable.innerVariables ?? [], this._score.submit, resetContextChain ? undefined : (lastElement(this.contextChain)?.data.findings.item));
            //Trigger a calculation simulation when changing the findings
            data.findings.setOnChanged(async (findings) => {
                ModuleFE_ExplainV2.simulate_ExpressionResult([expressionId], resolve_ProvideInfoFindings_EMFindings(findings.item), false, () => this.context.current.updateCalcInProgress(false));
                this.context.current.updateCalcInProgress(true);
            });
            this.openContext({ key: provideInfoContextKey_Score, data }, resetContextChain);
            //Make sure to close the graph if this starts a new chain
            if (resetContextChain)
                ModuleFE_AdvisorBoard.setColumnItems(ColumnKey_LeftColumn, LeftColumn_AllClosed);
            //Simulate calculation
            ModuleFE_ExplainV2.simulate_ExpressionResult([expressionId], resolve_ProvideInfoFindings_EMFindings(data.findings.item), true)
                .then(response => this._score.handleSimulationResponse(response))
                .catch(err => this.logError('Failed to simulate score', err));
        },
        handleSimulationResponse: (response, closeInputIfScopeIsEmpty = false) => {
            const currentContext = lastElement(this.contextChain);
            if (currentContext?.key !== provideInfoContextKey_Score)
                return;
            const data = currentContext.data;
            const calculationResult = response.calculationResults[data.expression._id];
            if (!calculationResult)
                return;
            data.calculationInProgress = false;
            data.calculationResult = calculationResult;
            data.partialCalculationFindings = this.getPartialCalculationFindings(data, response);
            if (data.expressionDescription)
                data.valueTree = calculate_ExpressionValueTree(data.expressionDescription, data.calculationResult);
            this.context.utils.recalculateInputScope(currentContext);
            if (closeInputIfScopeIsEmpty && !data.input.scope?.length) {
                data.input.show = false;
                data.input.varId = undefined;
            }
            else if (data.input.scope?.length) {
                data.input.varId = data.input.scope[0];
            }
            dispatch_OnProvideInfoContextUpdate.dispatchAll(currentContext);
        },
        wrapUp: async (context, previousContext) => {
            if (context.key !== provideInfoContextKey_Score)
                throw new BadImplementationException('Called score wrap up with non score context');
            const scoreExpressionId = context.data.expression._id;
            const findings = context.data.findings.item;
            const previousFindings = previousContext.data.findings.item;
            //Simulate answer and update previous context
            const normalizedEMFindings = resolve_ProvideInfoFindings_EMFindings(findings);
            const response = await ModuleFE_ExplainV2.simulate_ExpressionResult([scoreExpressionId], normalizedEMFindings, true);
            supplementPreviousContextFindings(findings, previousFindings, response.findings);
            context.data.findings.setOnChanged(voidFunction);
        },
        submit_AsLevel: async (findings) => {
            const currentContext = lastElement(this.contextChain);
            const previousContext = this.contextChain[this.contextChain.length - 2];
            if (!previousContext)
                throw new BadImplementationException('Submitting score as a level with no previous context!');
            try {
                await this._score.wrapUp(currentContext, previousContext);
                this.context.current.close(contextCloseMode_Force);
                return findings;
            }
            catch (err) {
                this.logError('Failed simulating score as level', err);
                return findings;
            }
        },
        submit_AsMain: async (findings) => {
            try {
                //Submit findings to update encounter
                await ModuleFE_EMInterface.updateEncounterApis.updateNeededVariables({ findings: resolve_ProvideInfoFindings_EMFindings(findings) });
                this.closeContextChain(contextCloseMode_Force);
                return findings;
            }
            catch (err) {
                this.logError('Failed submitting score context', err);
                return findings;
            }
        },
        submit: async (findings) => {
            const currentContext = lastElement(this.contextChain);
            if (currentContext?.key !== provideInfoContextKey_Score)
                throw new BadImplementationException(`Called score-submit with non score current context: ${currentContext?.key}`);
            ModuleFE_ExplainV2.clearSimulations();
            if (this.contextChain?.length <= 1)
                return this._score.submit_AsMain(findings);
            return this._score.submit_AsLevel(findings);
        }
    };
    //######################### Logic - Protocol #########################
    _protocol = {
        open: (protocolId, navMode = false, showDetails = false) => {
            const start = Date.now();
            this.showDetailsOnOpen = showDetails;
            if (this.contextChain?.[0].key === 'protocol' && this.contextChain[0].data.protocolData.protocol._id === protocolId) {
                if (showDetails)
                    return dispatch_OnProvideInfoContextUpdate.dispatchAll(this.contextChain[0]);
                return this.closeContextChain(contextCloseMode_Safe);
            }
            const data = getProvideInfoContextData_Protocol(protocolId, navMode, this._protocol.submit);
            if (!data)
                return;
            ModuleFE_ExplainV2.clearSimulations();
            //Trigger a navigation simulation when changing the findings
            data.findings.setOnChanged(async (findings) => {
                const resolvedEMFindings = resolve_ProvideInfoFindings_EMFindings(findings.item);
                ModuleFE_ExplainV2.simulate_ProtocolNavigation(data.protocolData.pathway._id, [], resolvedEMFindings, false, () => this.context.current.updateCalcInProgress(false));
                this.context.current.updateCalcInProgress(true);
            });
            //Opening a protocol context always starts a new context chain
            this.explainMode = !ModuleFE_AdvisorMini.getIsMiniActive();
            this.openContext({ key: provideInfoContextKey_Protocol, data }, true);
            //Open graph panel
            ModuleFE_AdvisorBoard.setColumnItems(ColumnKey_LeftColumn, protocolContext_LeftColumn);
            if (data.navMode) {
                ModuleFE_ExplainV2.simulate_ProtocolNavigation(data.protocolData.pathway._id, [], resolve_ProvideInfoFindings_EMFindings(data.findings.item), true)
                    .then(response => this._protocol.handleSimulationResponse(response))
                    .catch(err => this.logError('Failed to simulate protocol', err));
            }
            ModuleFE_AdvisorAnalytics.trigger.navigation.navigation_protocolOpened({
                protocolId,
                protocolLabel: data.protocolData.getLabel(),
                elapsedMs: Date.now() - start,
            });
        },
        toggleStep: (step) => {
            const context = this.getBaseContext();
            if (context?.key !== 'protocol')
                return;
            context.data.openedSteps[step.relatedExpression._id] = !context.data.openedSteps[step.relatedExpression._id];
            //Recalculate the scope
            this.context.utils.recalculateInputScope(context);
            //Open input on first item in scope if there is no current input
            if (!context.data.input.varId) {
                const varId = context.data.input.scope?.[0];
                if (varId) {
                    context.data.input.varId = varId;
                    context.data.input.show = true;
                }
            }
            dispatch_OnProvideInfoContextUpdate.dispatchAll(context);
        },
        focusStep2: (transitionId) => {
            const context = this.getBaseContext();
            if (context?.key !== 'protocol')
                return false;
            const step = context.data.nextSteps.find(i => i.transition._id === transitionId);
            if (!step)
                return false;
            context.data.openedSteps = {};
            this._protocol.toggleStep(step);
            return true;
        },
        handleSimulationResponse: (response, closeInputIfScopeIsEmpty = false) => {
            const currentContext = lastElement(this.contextChain);
            if (currentContext?.key !== provideInfoContextKey_Protocol)
                return;
            //Find go to res function id
            const updatedConditions = response.goToTransition?.updatedTransitionConditions ?? {};
            const goToTransitionId = _keys(updatedConditions).find(key => {
                return updatedConditions[key] === TransitionCondition.GO_TO_TRANSITION;
            });
            const data = currentContext.data;
            data.calculationInProgress = false;
            data.partialCalculationFindings = this.getPartialCalculationFindings(data, response);
            data.nextSteps = data.nextSteps.map(step => {
                const calculationResult = response.calculationResults[step.relatedExpression._id];
                let valueTree;
                if (step.expressionDescription)
                    valueTree = calculate_ExpressionValueTree(step.expressionDescription, calculationResult);
                //UI-HACK:
                //As "true" resolving expressions will always be RES_FUNC_TRUE if they aren't go-to,
                //We swap their status to RES_FUNC_FALSE if any other step transition is the go-to to
                //show that the "else" condition has failed
                let condition = response.goToTransition.updatedTransitionConditions[step.transition._id];
                if (step.relatedExpression.expression?.toLowerCase() === 'true'
                    && exists(goToTransitionId)
                    && goToTransitionId !== step.transition._id)
                    condition = TransitionCondition.RES_FUNC_FALSE;
                return {
                    ...step,
                    calculationResult,
                    valueTree,
                    transitionCondition: condition,
                    ordersMeetsExpressionCondition: response.ordersMeetsExpressionCondition,
                };
            });
            this.context.utils.recalculateInputScope(currentContext);
            if (closeInputIfScopeIsEmpty && !data.input.scope?.length) {
                data.input.show = false;
                data.input.varId = undefined;
            }
            else if (data.input.scope?.length) {
                data.input.varId = data.input.scope[0];
            }
            dispatch_OnProvideInfoContextUpdate.dispatchAll(currentContext);
        },
        wrapUp: async (findings) => {
            try {
                //Submit findings to update encounter
                const normalizedFindings = resolve_ProvideInfoFindings_EMFindings(findings);
                const resolvedSuggestedFindings = resolve_EMSuggestedFindings(findings, normalizedFindings);
                //Mute toaster until after update encounter has resolved
                Toaster_AdvisorGeneral.setMute(true);
                this.protocolSubmissionInProgress = true;
                await ModuleFE_EMInterface.updateEncounterApis.updatedNeededVariablesWithSuggestions(normalizedFindings, {
                    accepted: resolvedSuggestedFindings.acceptedIds,
                    rejected: resolvedSuggestedFindings.rejectedIds
                });
            }
            catch (err) {
                throw new Error('Failed to wrap up protocol', err);
            }
            finally {
                Toaster_AdvisorGeneral.setMute(false);
                this.protocolSubmissionInProgress = false;
            }
        },
        submit: async (findings) => {
            //Clear all current triggered simulations
            ModuleFE_ExplainV2.clearSimulations();
            try {
                await this._protocol.wrapUp(findings);
                this.reOpenProtocolContext();
                return findings;
            }
            catch (err) {
                this.logError('Failed to submit protocol', err);
                return findings;
            }
        },
        focusStep: (selectedStepTransitionId) => {
            const protocolContext = this.contextChain?.[0];
            if (!protocolContext || protocolContext.key !== provideInfoContextKey_Protocol)
                return;
            protocolContext.data.selectedStepTransitionId = protocolContext.data.selectedStepTransitionId === selectedStepTransitionId
                ? undefined
                : selectedStepTransitionId;
            dispatch_OnProvideInfoContextUpdate.dispatchAll(protocolContext);
        },
        proceedWithStep: async (step, outerContext) => {
            const start = Date.now();
            const firstContext = this.contextChain?.[0];
            let data;
            if (firstContext?.key === provideInfoContextKey_Protocol)
                data = firstContext.data.protocolData;
            else
                data = outerContext;
            if (!data)
                return;
            const normalizedFindings = exists(firstContext)
                ? resolve_ProvideInfoFindings_EMFindings(firstContext.data.findings.item)
                : { findingsToUpdate: [], processedRawFindings: [] };
            try {
                //Mute toaster until after we proceed with step
                Toaster_AdvisorGeneral.setMute(true);
                this.protocolSubmissionInProgress = true;
                //Proceed with step
                await ModuleFE_EMInterface.vv1.navigator.manualNavigateOnTransition({
                    pathwayId: data.pathway._id,
                    transitionId: step.transition._id,
                    ...normalizedFindings,
                }).executeSync();
                const elapsedMs = Date.now() - start;
                const findings = firstContext?.key === provideInfoContextKey_Protocol
                    ? firstContext.data.findings.item
                    : undefined;
                let unresolvedQuestionCount = 0;
                let resolvedQuestionCount = 0;
                let inferredAnswerCount = 0;
                provideInfo_GetAllStepVariableIds(step).forEach(variableId => {
                    const { userValue, inferredValue } = findings
                        ? provideInfo_GetQuestionValues(findings, variableId)
                        : { userValue: undefined, inferredValue: undefined };
                    if (exists(userValue)) {
                        resolvedQuestionCount++;
                        return;
                    }
                    if (exists(inferredValue)) {
                        resolvedQuestionCount++;
                        inferredAnswerCount++;
                        return;
                    }
                    unresolvedQuestionCount++;
                });
                const ranked = ProtocolData.filterDisplayable(ProtocolData.sort(filterInstances(ModuleFE_RuntimeProtocol.cache.all().map(protocol => ProtocolData.createNew(protocol._id)))));
                ModuleFE_AdvisorAnalytics.trigger.navigation.navigation_advance({
                    protocolId: data.protocol._id,
                    protocolLabel: data.getLabel(),
                    elapsedMs,
                    stepId: step.originState._id,
                    stepLabel: step.originState.label,
                    nextStepId: step.targetState._id,
                    nextStepLabel: step.targetState.label,
                    diagnosisId: data.pathway._id,
                    diagnosisLabel: data.getLabel(),
                    diagnosisRankIndex: ranked.findIndex(item => item.protocol._id === data.protocol._id),
                    unresolvedQuestionCount,
                    resolvedQuestionCount,
                    inferredAnswerCount,
                    considerationLevel: data.getConsideration(),
                });
                //Close context
                this.closeContextChain(contextCloseMode_Force);
            }
            catch (err) {
                this.logInfo(err);
            }
            finally {
                Toaster_AdvisorGeneral.setMute(false);
                this.protocolSubmissionInProgress = false;
            }
        },
        toggleNavMode: (navMode) => {
            const baseContext = this.getCurrentProtocolContextData();
            if (!baseContext)
                return;
            if (exists(navMode) && baseContext.navMode === navMode)
                return;
            baseContext.navMode = navMode ?? !baseContext.navMode;
            dispatch_OnProvideInfoContextUpdate.dispatchAll(this.contextChain[0]);
        },
    };
    //######################### Public Logic - Explain #########################
    toggleExplain = () => {
        this.explainMode = !this.explainMode;
        dispatch_OnProvideInfoExplain.dispatchAll();
    };
    getExplainMode = () => this.explainMode;
    //######################### Public Logic - Context #########################
    refreshProtocolContextData = () => {
        const currentProtocolContext = this.contextChain?.[0];
        if (currentProtocolContext?.key !== 'protocol')
            return;
        const newData = ProtocolData.createNew(currentProtocolContext.data.protocolData.protocol._id);
        if (!newData)
            return;
        currentProtocolContext.data.protocolData = newData;
        dispatch_OnProvideInfoContextUpdate.dispatchAll(currentProtocolContext);
    };
    getLatestContext = () => {
        return lastElement(this.contextChain ?? []);
    };
    getBaseContext = () => this.contextChain?.[0];
    getCurrentProtocolContextData = () => {
        if (this.contextChain?.[0].key === provideInfoContextKey_Protocol)
            return this.contextChain[0].data;
    };
    shouldShowDetailsOnOpen = () => this.showDetailsOnOpen;
    clearShowDetailsOnOpen = () => {
        this.showDetailsOnOpen = false;
    };
    //######################### Public Logic - Interface #########################
    protocol = {
        open: this._protocol.open,
        toggleStep: this._protocol.toggleStep,
        focusStep: this._protocol.focusStep,
        focusStep2: this._protocol.focusStep2,
        proceedWithStep: this._protocol.proceedWithStep,
        toggleNavMode: this._protocol.toggleNavMode,
    };
    score = {
        open: this._score.open,
    };
    expression = {
        open: this._expression.open,
    };
    context = {
        utils: {
            recalculateInputScope: (context) => {
                switch (context.key) {
                    case provideInfoContextKey_Protocol: {
                        const openedSteps = context.data.nextSteps.filter(step => !!context.data.openedSteps[step.relatedExpression._id]);
                        context.data.input.scope = provideInfo_GetInputScope_Protocol(openedSteps, context.data.findings.item);
                        break;
                    }
                    case provideInfoContextKey_Score:
                    case provideInfoContextKey_Expression: {
                        context.data.input.scope = provideInfo_GetInputScope_Expression(context.data.expression, context.data.expressionDescription, context.data.valueTree, context.data.findings.item);
                        break;
                    }
                }
            }
        },
        current: {
            selectVariable: (variableId) => {
                const currentContext = this.getLatestContext();
                if (!currentContext)
                    return;
                currentContext.data.input.varId = variableId;
                currentContext.data.input.show = true;
                //Recalculate the scope
                this.context.utils.recalculateInputScope(currentContext);
                dispatch_OnProvideInfoContextUpdate.dispatchAll(currentContext);
            },
            hideInput: () => {
                const currentContext = this.getLatestContext();
                if (!currentContext)
                    return;
                currentContext.data.input.show = false;
                currentContext.data.input.varId = undefined;
                dispatch_OnProvideInfoContextUpdate.dispatchAll(currentContext);
            },
            updateValue: (variableId, value) => {
                const currentContext = this.getLatestContext();
                if (!currentContext)
                    return;
                const variable = ModuleFE_Advisor_Variable.cache.unique(variableId);
                if (!variable)
                    return;
                if (variable.type === variableType_AttributeInstance)
                    return this.context.current.updateAttribute(variable, value);
                let editable;
                const prfIndex = currentContext.data.findings.item.toCreate?.findIndex(i => i.variableId === variableId);
                if (exists(prfIndex) && prfIndex !== -1) {
                    editable = currentContext.data.findings.editProp('toCreate', []).editProp(prfIndex, {});
                }
                else {
                    const ftuIndex = currentContext.data.findings.item.toUpdate?.findIndex(i => i.variableId === variableId);
                    if (exists(ftuIndex) && ftuIndex !== -1) {
                        editable = currentContext.data.findings.editProp('toUpdate', []).editProp(ftuIndex, {});
                    }
                }
                if (!editable)
                    return this.logWarning(`Variable out of current context: ${variableId}`);
                switch (editable.item.type) {
                    case 'numeric':
                        switch (typeof value) {
                            case 'number':
                            case 'undefined':
                                return editable.updateObj({ value, source: findingSourceTypesEnum.ADVISOR });
                            default:
                                return this.logWarning(`Value type mismatch: findingType="numeric", valueType="${typeof value}"`);
                        }
                    case 'enumerated':
                        switch (typeof value) {
                            case 'string':
                            case 'undefined': {
                                const updatedFields = {
                                    valueIds: value ? [value] : undefined,
                                    source: findingSourceTypesEnum.ADVISOR,
                                    suggestedFinding: editable.item.suggestedFinding,
                                };
                                if (updatedFields.suggestedFinding)
                                    updatedFields.suggestedFinding.userInteracted = true;
                                return editable.updateObj(updatedFields);
                            }
                            default:
                                return this.logWarning(`Value type mismatch: findingType="enumerated", valueType="${typeof value}"`);
                        }
                }
            },
            updateAttribute: (attribute, value) => {
                let parentEditable;
                const currentContext = this.getLatestContext();
                if (!currentContext)
                    return;
                if (typeof value === 'number')
                    return this.logWarning(`Value type mismatch: findingType="enumerated", valueType="${typeof value}"`);
                const ftuIndex = currentContext.data.findings.item.toUpdate?.findIndex(i => i.variableId === attribute.parents.variableId);
                if (exists(ftuIndex) && ftuIndex !== -1) {
                    parentEditable = currentContext.data.findings.editProp('toUpdate', []).editProp(ftuIndex, {});
                }
                else {
                    const prfIndex = currentContext.data.findings.item.toCreate?.findIndex(i => i.variableId === attribute.parents.variableId);
                    if (exists(prfIndex) && prfIndex !== -1)
                        parentEditable = currentContext.data.findings.editProp('toCreate', []).editProp(prfIndex, {});
                }
                if (!parentEditable)
                    return this.logWarning(`Parent variable out of current context: ${attribute.parents.variableId}`);
                const attributes = parentEditable.editProp('attributes', []);
                const attIndex = attributes.item.findIndex(i => i?.variableId === attribute._id);
                if (attIndex === -1)
                    return attributes.updateArrayAt({ type: 'enumerated', variableId: attribute._id, valueIds: exists(value) ? [value] : [] });
                const editable = attributes.editProp(attIndex, {});
                editable.updateObj({ valueIds: exists(value) ? [value] : [] });
            },
            isBaseContext: () => this.contextChain?.length === 1,
            close: (mode) => {
                if (!this.contextChain?.length)
                    return;
                if (this.contextChain?.length === 1)
                    return this.closeContextChain(mode);
                //Pop last context from the chain
                this.contextChain.pop();
                //Dispatch newest last context as current
                const currentContext = lastElement(this.contextChain);
                //Recalculate input scope
                if (currentContext)
                    this.context.utils.recalculateInputScope(currentContext);
                dispatch_OnProvideInfoContextChange.dispatchAll(currentContext);
                //Trigger simulation
                ModuleFE_ExplainV2.clearSimulations();
                this.muteSimulationListeners = true;
                switch (currentContext?.key) {
                    case provideInfoContextKey_Protocol: {
                        if (!currentContext.data.navMode)
                            return;
                        return ModuleFE_ExplainV2.simulate_ProtocolNavigation(currentContext.data.protocolData.pathway._id, [], resolve_ProvideInfoFindings_EMFindings(currentContext.data.findings.item), true)
                            .then(response => {
                            this._protocol.handleSimulationResponse(response, true);
                            this.muteSimulationListeners = false;
                        })
                            .catch(err => this.logError('Failed to simulate protocol', err));
                    }
                    case provideInfoContextKey_Score: {
                        return ModuleFE_ExplainV2.simulate_ExpressionResult([currentContext.data.expression._id], resolve_ProvideInfoFindings_EMFindings(currentContext.data.findings.item), true)
                            .then(response => {
                            this._score.handleSimulationResponse(response, true);
                            this.muteSimulationListeners = false;
                        })
                            .catch(err => this.logError('Failed to simulate score', err));
                    }
                    case provideInfoContextKey_Expression: {
                        return ModuleFE_ExplainV2.simulate_ExpressionResult([currentContext.data.expression._id], resolve_ProvideInfoFindings_EMFindings(currentContext.data.findings.item), true)
                            .then(response => {
                            this._expression.handleSimulationResponse(response, true);
                            this.muteSimulationListeners = false;
                        })
                            .catch(err => this.logError('Failed to simulate expression', err));
                    }
                }
            },
            updateCalcInProgress: (calcInProgress) => {
                const context = this.getLatestContext();
                if (!context || context.data.calculationInProgress === calcInProgress)
                    return;
                context.data.calculationInProgress = calcInProgress;
                dispatch_OnProvideInfoContextUpdate.dispatchAll(context);
            }
        },
        chain: {
            hasChanges: () => {
                if (!this.contextChain?.length)
                    return false;
                return this.contextChain.some(context => context.data.findings.hasChanges());
            },
            close: this.closeContextChain
        }
    };
}
export const ModuleFE_ProvideInfo = new ModuleFE_ProvideInfo_Class();
//# sourceMappingURL=ModuleFE_ProvideInfo.js.map